import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Mobile mega menu drawer with sliding-panel drilldown navigation.
 *
 * Root view lists all top-level entries; each drillable entry pushes a panel view
 * over the root with a horizontal slide. Back-arrow returns to the root.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - Native details wrapping the trigger + drawer
 * @property {HTMLDivElement} container - Drawer container (full-screen)
 * @property {HTMLDivElement} viewport - Clipping viewport for sliding views
 * @property {HTMLDivElement} rootView - Root view (top-level link list)
 * @property {HTMLElement[]} panelViews - One view per drillable link
 * @property {HTMLElement} title - Aria-live title in the drawer header
 * @property {HTMLButtonElement} backBtn - Back button in the drawer header
 *
 * @extends {Component<Refs>}
 */
class AxMegaMenuDrawer extends Component {
  requiredRefs = ['details', 'container', 'viewport', 'rootView', 'title', 'backBtn'];

  /** @type {string | null} */
  #activeHandle = null;

  /** @type {string} */
  #rootTitle = '';

  /** @type {WeakMap<HTMLElement, number>} - Per-view scroll position memory */
  #scrollMemory = new WeakMap();

  /** @type {HTMLElement | null} - Trigger element to restore focus to on back */
  #lastTrigger = null;

  /** @type {Promise<Document> | null} - Cached fetch promise (single request per page) */
  #fetchPromise = null;

  /** @type {AbortController | null} */
  #fetchAbortController = null;

  /** @type {boolean} */
  #injected = false;

  get sectionId() {
    const wrapper = this.closest('[id^="shopify-section-"]');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#rootTitle = this.refs.rootView.dataset.title || '';
    this.refs.title.textContent = this.#rootTitle;
    this.addEventListener('keyup', this.#onKeyUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    removeTrapFocus();
    this.#fetchAbortController?.abort();
  }

  /** @returns {boolean} */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.isOpen) return;
    if (this.#activeHandle) {
      this.drillBack();
    } else {
      this.close();
    }
  };

  /**
   * Toggle the drawer open/closed.
   * Suppresses the native `<summary>` toggle so JS owns the open/close lifecycle
   * (animations, focus trap, drilldown reset) end-to-end.
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the drawer.
   */
  open() {
    this.refs.details.setAttribute('open', '');
    const summary = this.refs.details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'true');

    // Fire-and-forget — runs in parallel with the slide-in animation so panel
    // content is usually ready by the time the user drills into one.
    this.#loadMenuContent().catch(() => {});

    requestAnimationFrame(() => {
      this.refs.container.classList.add('is-open');
      onAnimationEnd(this.refs.container, () => trapFocus(this.refs.container), { subtree: false });
    });
  }

  /**
   * Close the mobile drawer and open the sidecart drawer.
   */
  openCart() {
    this.close();
    const cartDrawer = /** @type {{ open?: () => void } | null} */ (
      document.querySelector('cart-drawer-component')
    );
    cartDrawer?.open?.();
  }

  /**
   * Close the drawer entirely.
   */
  close() {
    const summary = this.refs.details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    this.refs.container.classList.remove('is-open');

    onAnimationEnd(
      this.refs.container,
      () => {
        if (!this.refs) return;
        this.refs.details.removeAttribute('open');
        removeTrapFocus();
        this.#resetToRoot();
      },
      { subtree: false }
    );
  }

  /**
   * Drill into a panel view by handle.
   * @param {string} handle
   * @param {Event} event
   */
  drillTo(handle, event) {
    if (!handle) return;
    if (event?.target instanceof HTMLElement) {
      this.#lastTrigger = event.target.closest('[on\\:click]');
    }

    const panel = this.#getPanelByHandle(handle);
    if (!panel) return;

    // Save root scroll position
    this.#scrollMemory.set(this.refs.rootView, this.refs.rootView.scrollTop);

    // Deactivate any other panel
    for (const view of this.refs.panelViews ?? []) {
      if (view !== panel) {
        view.removeAttribute('data-active');
        view.setAttribute('inert', '');
      }
    }

    panel.setAttribute('data-active', '');
    panel.removeAttribute('inert');
    this.refs.rootView.setAttribute('inert', '');
    this.dataset.drilled = '';
    this.#activeHandle = handle;

    // Ensure the panel content is loaded — fetch is normally already in flight from open(),
    // but we await it here so a fast drill still gets the content.
    this.#loadMenuContent()
      .then(() => this.#hydrateLazyMediaWhenSettled(panel))
      .catch(() => {});

    // Restore previous scroll if any
    panel.scrollTop = this.#scrollMemory.get(panel) ?? 0;

    this.refs.title.textContent = panel.dataset.title || '';
    this.refs.backBtn.hidden = false;

    // Move focus to the first focusable element in the panel for keyboard users.
    const firstFocusable = panel.querySelector('a, button:not([disabled]), input:not([disabled])');
    if (firstFocusable instanceof HTMLElement) {
      firstFocusable.focus({ preventScroll: true });
    }
  }

  /**
   * Drill back to the root view.
   */
  drillBack() {
    const activePanel = this.#activeHandle ? this.#getPanelByHandle(this.#activeHandle) : null;
    if (activePanel) {
      this.#scrollMemory.set(activePanel, activePanel.scrollTop);
      activePanel.removeAttribute('data-active');
      activePanel.setAttribute('inert', '');
    }

    this.refs.rootView.removeAttribute('inert');
    delete this.dataset.drilled;
    this.#activeHandle = null;
    this.refs.title.textContent = this.#rootTitle;
    this.refs.backBtn.hidden = true;
    this.refs.rootView.scrollTop = this.#scrollMemory.get(this.refs.rootView) ?? 0;

    if (this.#lastTrigger instanceof HTMLElement) {
      this.#lastTrigger.focus({ preventScroll: true });
      this.#lastTrigger = null;
    }
  }

  /**
   * Reset to root state without animation, used after close().
   */
  #resetToRoot() {
    if (!this.refs) return;
    for (const view of this.refs.panelViews ?? []) {
      view.removeAttribute('data-active');
      view.setAttribute('inert', '');
    }
    this.refs.rootView?.removeAttribute('inert');
    delete this.dataset.drilled;
    this.#activeHandle = null;
    if (this.refs.title) this.refs.title.textContent = this.#rootTitle;
    if (this.refs.backBtn) this.refs.backBtn.hidden = true;
  }

  /**
   * Fetch the mega menu content via Section Rendering API (cached for the page lifetime)
   * and inject it into the placeholders. Idempotent.
   * @returns {Promise<void>}
   */
  async #loadMenuContent() {
    if (this.#injected) return;

    if (!this.#fetchPromise) {
      const sectionId = this.sectionId;
      if (!sectionId) return;

      this.#fetchAbortController = new AbortController();
      const is404 = /** @type {any} */ (window).routes?.template_name === '404';
      const path = is404 ? '/' : window.location.pathname;
      const url = `${path}?section_id=${sectionId}&lazy=1`;

      this.#fetchPromise = fetch(url, { signal: this.#fetchAbortController.signal })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.text();
        })
        .then((html) => new DOMParser().parseFromString(html, 'text/html'))
        .catch((err) => {
          this.#fetchPromise = null;
          throw err;
        });
    }

    try {
      const doc = await this.#fetchPromise;
      if (this.#injected) return;
      this.#injectMenuContent(doc);
      this.#injected = true;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('[ax-mega-menu-drawer] mega menu fetch failed:', error);
      }
    }
  }

  /**
   * Inject fetched panel content into placeholders, matching by `data-menu-handle`.
   * Also adopts `<style data-section-stylesheet>` from the response into <head>
   * (Shopify no longer bundles unrendered snippet stylesheets into styles.css).
   * @param {Document} doc
   */
  #injectMenuContent(doc) {
    if (!document.querySelector('style[data-mega-menu-stylesheet]')) {
      for (const style of doc.querySelectorAll('style[data-section-stylesheet]')) {
        const clone = /** @type {HTMLStyleElement} */ (style.cloneNode(true));
        clone.removeAttribute('data-section-stylesheet');
        clone.setAttribute('data-mega-menu-stylesheet', '');
        document.head.appendChild(clone);
      }
    }

    const incoming = doc.querySelectorAll('.mega-menu__content[data-menu-handle]');
    for (const newContent of incoming) {
      const handle = newContent.getAttribute('data-menu-handle');
      if (!handle) continue;
      const target = /** @type {HTMLElement | null} */ (
        this.querySelector(`.mega-menu__content[data-menu-handle="${CSS.escape(handle)}"]`)
      );
      if (!target) continue;
      target.innerHTML = newContent.innerHTML;
      target.removeAttribute('aria-busy');
      target.dataset.state = 'loaded';
      target.classList.remove('mega-menu__content--placeholder');
    }
  }

  /**
   * Dispatch `reactivateLazyObserver` once the slide-in transition has settled, so
   * the IntersectionObserver re-evaluates against the post-transform layout.
   * Falls back to a 350ms timeout in case `transitionend` doesn't fire (reduced motion).
   * @param {HTMLElement} panel
   */
  #hydrateLazyMediaWhenSettled(panel) {
    let dispatched = false;
    const dispatch = () => {
      if (dispatched) return;
      dispatched = true;
      document.dispatchEvent(
        new CustomEvent('reactivateLazyObserver', {
          detail: { lazyParentElement: panel },
        })
      );
    };

    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'transform') return;
      panel.removeEventListener('transitionend', onEnd);
      dispatch();
    };

    panel.addEventListener('transitionend', onEnd);
    setTimeout(() => {
      panel.removeEventListener('transitionend', onEnd);
      dispatch();
    }, 350);
  }

  /**
   * @param {string} handle
   * @returns {HTMLElement | null}
   */
  #getPanelByHandle(handle) {
    const views = this.refs.panelViews;
    if (!views) return null;
    for (const view of views) {
      if (view.dataset.handle === handle) return view;
    }
    return null;
  }
}

if (!customElements.get('ax-mega-menu-drawer')) {
  customElements.define('ax-mega-menu-drawer', AxMegaMenuDrawer);
}
